<template>
  <div>
    <page-header
      :title="$t('drivers_report')"
      :subtitle="$t('drivers_report_subtitle')"
      icon="mdi-chart-box-outline"
      icon-color="primary"
    >
      <template #actions v-if="!device.isMobile">
        <circle-button
          :loading="isLoading"
          @click="fetchStats"
          icon="mdi-refresh"
          color="primary"
          :small="false"
        />
      </template>
    </page-header>

    <!-- KPI summary cards -->
    <div class="px-6 mt-4">
      <v-row>
        <v-col cols="6" sm="3">
          <v-card outlined class="ordera-box-shadow pa-4 d-flex align-center">
            <v-avatar color="primary" size="44" class="mr-3">
              <v-icon color="white">mdi-account-hard-hat</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold">{{ totalDrivers }}</div>
              <div class="text-caption grey--text">{{ $t("total_drivers") }}</div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="ordera-box-shadow pa-4 d-flex align-center">
            <v-avatar color="success" size="44" class="mr-3">
              <v-icon color="white">mdi-check-decagram</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold">{{ activeDrivers }}</div>
              <div class="text-caption grey--text">{{ $t("active_drivers") }}</div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="ordera-box-shadow pa-4 d-flex align-center">
            <v-avatar color="secondary" size="44" class="mr-3">
              <v-icon color="white">mdi-truck-fast-outline</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold">{{ totalDeliveries }}</div>
              <div class="text-caption grey--text">{{ $t("total_deliveries") }}</div>
            </div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="3">
          <v-card outlined class="ordera-box-shadow pa-4 d-flex align-center">
            <v-avatar color="orange" size="44" class="mr-3">
              <v-icon color="white">mdi-cash-multiple</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold">
                {{ totalDeliveredValue }} {{ restaurantCurrency }}
              </div>
              <div class="text-caption grey--text">
                {{ $t("total_delivered_value") }}
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <page-toolbar
      v-if="!device.isMobile"
      :search.sync="search"
      :search-placeholder="$t('search_drivers_placeholder')"
      searchable
      :loading="isLoading"
    />

    <!-- Desktop View -->
    <template v-if="!device.isMobile">
      <div class="px-6 mt-2">
        <v-data-table
          :headers="headers"
          :items="filteredRows"
          :loading="isLoading"
          :loading-text="$t('loading_data')"
          class="striped-table ordera-box-shadow"
        >
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center py-2">
              <v-avatar color="primary" size="32" class="mr-2">
                <v-icon color="white" size="18">mdi-account</v-icon>
              </v-avatar>
              <div class="font-weight-medium">{{ item.name }} {{ item.lastname }}</div>
            </div>
          </template>
          <template v-slot:item.vehicle_types="{ item }">
            <v-chip
              v-for="vehicle in getVehicleTypes(item.vehicle_types)"
              :key="vehicle.value"
              small
              outlined
              color="primary"
              class="mr-1 mb-1"
            >
              <v-icon small left>{{ vehicle.icon }}</v-icon>
              {{ $t(vehicle.labelKey) }}
            </v-chip>
          </template>
          <template v-slot:item.active="{ item }">
            <v-icon :color="item.active ? 'success' : 'grey'">
              {{ item.active ? "mdi-check-circle" : "mdi-close-circle" }}
            </v-icon>
          </template>
          <template v-slot:item.deliveries_completed="{ item }">
            <span class="font-weight-medium">{{ item.deliveries_completed }}</span>
          </template>
          <template v-slot:item.total_distance_km="{ item }">
            {{ item.total_distance_km }} km
          </template>
          <template v-slot:item.total_delivered_value="{ item }">
            {{ item.total_delivered_value }} {{ restaurantCurrency }}
          </template>
          <template v-slot:item.average_rating="{ item }">
            <div class="d-flex align-center justify-center">
              <v-icon small color="amber darken-2" class="mr-1">mdi-star</v-icon>
              {{ item.average_rating }}
            </div>
          </template>
          <template v-slot:item.last_delivery_date="{ item }">
            <span v-if="item.last_delivery_date">{{ item.last_delivery_date }}</span>
            <span v-else class="grey--text">-</span>
          </template>
        </v-data-table>
      </div>
    </template>

    <!-- Mobile View -->
    <template v-else>
      <div class="px-3 mt-3">
        <mobile-data-cards
          :headers="mobileHeaders"
          :items="filteredRows"
          :loading="isLoading"
          item-key="driver_id"
          show-filters
          :show-row-number="false"
          :fields-per-row="2"
          :bold-fields="['name']"
        >
          <template v-slot:item.name="{ item }">
            <div class="d-flex align-center">
              <v-avatar color="primary" size="24" class="mr-2">
                <v-icon color="white" size="14">mdi-account</v-icon>
              </v-avatar>
              {{ item.name }} {{ item.lastname }}
            </div>
          </template>
          <template v-slot:item.vehicle_types="{ item }">
            <div v-for="vehicle in getVehicleTypes(item.vehicle_types)" :key="vehicle.value">
              <v-icon small left>{{ vehicle.icon }}</v-icon>
              {{ $t(vehicle.labelKey) }}
            </div>
          </template>
          <template v-slot:item.deliveries_completed="{ item }">
            {{ item.deliveries_completed }}
          </template>
          <template v-slot:item.total_delivered_value="{ item }">
            {{ item.total_delivered_value }} {{ restaurantCurrency }}
          </template>
        </mobile-data-cards>
      </div>
    </template>
  </div>
</template>

<script>
import PageToolbar from "@/components/global/PageToolbar.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import {
  generateMockDrivers,
  generateMockDriverStats,
  getVehicleTypes,
} from "@/data/supplierDrivers";

export default {
  name: "SupplierDriversReport",
  components: {
    PageToolbar,
    MobileDataCards,
  },
  data: () => ({
    search: "",
    isLoading: false,
    drivers: [],
    stats: [],
  }),
  computed: {
    restaurantCurrency() {
      return this.$store.state.restaurant?.settings?.price || "KM";
    },
    rows() {
      return this.drivers.map((driver) => ({
        ...driver,
        ...(this.stats.find((s) => s.driver_id === driver.id) || {}),
      }));
    },
    filteredRows() {
      if (!this.search) return this.rows;
      const search = this.search.toLowerCase();
      return this.rows.filter(
        (row) =>
          row.name?.toLowerCase().includes(search) ||
          row.lastname?.toLowerCase().includes(search)
      );
    },
    totalDrivers() {
      return this.drivers.length;
    },
    activeDrivers() {
      return this.drivers.filter((d) => d.active).length;
    },
    totalDeliveries() {
      return this.stats.reduce((sum, s) => sum + (s.deliveries_completed || 0), 0);
    },
    totalDeliveredValue() {
      return this.stats
        .reduce((sum, s) => sum + (s.total_delivered_value || 0), 0)
        .toFixed(2);
    },
    headers() {
      return [
        { text: this.$t("title"), value: "name", align: "start", width: "220px" },
        {
          text: this.$t("vehicle_type"),
          value: "vehicle_types",
          align: "start",
          width: "200px",
        },
        { text: this.$t("active"), value: "active", align: "center", width: "100px" },
        {
          text: this.$t("deliveries_completed"),
          value: "deliveries_completed",
          align: "end",
        },
        {
          text: this.$t("deliveries_canceled"),
          value: "deliveries_canceled",
          align: "end",
        },
        {
          text: this.$t("total_distance_km"),
          value: "total_distance_km",
          align: "end",
        },
        {
          text: this.$t("total_delivered_value"),
          value: "total_delivered_value",
          align: "end",
        },
        {
          text: this.$t("average_rating"),
          value: "average_rating",
          align: "center",
        },
        {
          text: this.$t("last_delivery_date"),
          value: "last_delivery_date",
          align: "center",
        },
      ];
    },
    mobileHeaders() {
      return [
        { text: this.$t("title"), value: "name", cols: 6 },
        { text: this.$t("vehicle_type"), value: "vehicle_types", cols: 6 },
        { text: this.$t("deliveries_completed"), value: "deliveries_completed", cols: 6 },
        {
          text: this.$t("total_delivered_value"),
          value: "total_delivered_value",
          cols: 6,
        },
      ];
    },
  },
  created() {
    this.fetchStats();
  },
  methods: {
    getVehicleTypes,
    fetchStats() {
      // No API available yet for supplier delivery statistics, so we generate
      // deterministic mock data locally to preview this report.
      this.isLoading = true;
      setTimeout(() => {
        this.drivers = generateMockDrivers();
        this.stats = generateMockDriverStats(this.drivers);
        this.isLoading = false;
      }, 400);
    },
  },
};
</script>
